import { Box, SimpleGrid, useToast } from '@chakra-ui/react';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { SingleField } from '@duke-hq/ui/components/SingleField/SingleField';
import { FormModalWrapper } from '@duke-hq/ui/components/Modals/FormModalWrapper';
import { normalisePostcodeForDisplay } from '@duke-hq/google-address/services/transform/postcode';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { useCallback, useState } from 'react';
import { isPostcodeConfirmationError } from '@duke-hq/google-address/services/validation/postcodeConfirmationError';
import { SectionHeading } from '../../components/SectionHeading';
import AddressAutocomplete, { type AddressAutocompleteFormData } from './AddressAutocomplete';

import { useTenantContext } from '../../TenantContext';
import type { States } from '@duke-hq/svc-api-db/schemas/enums/States.schema';

interface AddressFormData {
  studentId: string;
  addressFields: {
    addressInput?: string | undefined;
    addressState: States | undefined;
    addressLocality: string | undefined;
    addressStreetName: string | undefined;
    addressStreetNumber: string | undefined;
    addressStreetSubpremise: string | undefined;
    addressPostcode: string | undefined;
    formattedAddress: string | undefined;
    sameAsCurrent?: boolean;
    postalAddressStreetName: string | undefined;
    postalAddressLocality: string | undefined;
    postalAddressState: States | undefined;
    postalAddressPostcode: string | undefined;
    postalAddressStreetSubpremise: string | undefined;
  };
  postcodeWarnings?: { addressWarning: string | null; postalWarning: string | null };
  shouldRefetchAvetmiss?: boolean;
}

export default function AddressDetailsView({
  studentId,
  addressFields,
  postcodeWarnings,
  shouldRefetchAvetmiss = false,
}: AddressFormData): React.ReactElement {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const toast = useToast();
  const trpcUtils = trpcAdmin.useUtils();

  const ctx = useTenantContext();
  const googleAddressApiKey = ctx?.apis.GOOGLE_ADDRESS_VALIDATION_API_KEY ?? '';

  const updateAddressInfo = trpcAdmin.students.updateStudentAddressInfo.useMutation({
    onSuccess: async () => {
      toast({
        title: t`Student details updated successfully.`,
        status: 'success',
        duration: 7000,
        isClosable: true,
      });
      setIsModalOpen(false);
      await trpcUtils.students.byId.refetch({ id: studentId });
    },
    onError: (error) => {
      // Not a failure: AddressForm turns this rejection into its inline "save anyway" warning
      if (isPostcodeConfirmationError(error)) {
        return;
      }
      toast({
        title: t`Error updating student details.`,
        description: error instanceof Error ? error.message : t`Failed to update student details`,
        status: 'error',
        duration: 7000,
        isClosable: true,
      });
    },
    onSettled: () => {
      if (shouldRefetchAvetmiss) {
        void trpcUtils.avetmiss.sync.refetch();
      }
    },
  });

  const getAddressSuggestions = useCallback(
    (input: { addressLocality: string; addressState: string; addressPostcode: string }) =>
      trpcUtils.students.suggestAddressCorrections.fetch(input),
    [trpcUtils],
  );

  const handleUpdate = useCallback(
    async (values: AddressAutocompleteFormData) => {
      await updateAddressInfo.mutateAsync({
        id: studentId,
        formattedAddress: values.formattedAddress ?? '',
        addressStreetSubpremise: values.addressStreetSubpremise,
        addressStreetNumber: values.addressStreetNumber ?? '',
        addressStreetName: values.addressStreetName ?? '',
        addressState: values.addressState ?? '',
        addressPostcode: values.addressPostcode ?? '',
        addressLocality: values.addressLocality ?? '',
        postalAddressStreetName: values.postalAddressStreetName,
        postalAddressStreetSubpremise: values.postalAddressStreetSubpremise,
        postalAddressLocality: values.postalAddressLocality,
        postalAddressState: values.postalAddressState,
        postalAddressPostcode: values.postalAddressPostcode,
        confirmedAddressWarnings: values.confirmedAddressWarnings,
      });
    },
    [studentId, updateAddressInfo],
  );

  return (
    <Box>
      <SectionHeading onEdit={() => setIsModalOpen(true)} editDetails={true}>
        <Trans>Address</Trans>
      </SectionHeading>
      <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} spacing={4} mb={4}>
        <SingleField
          name={<Trans>Street</Trans>}
          value={
            [addressFields.addressStreetNumber, addressFields.addressStreetName]
              .filter(Boolean)
              .join(' ') || undefined
          }
        />
        <SingleField name={<Trans>City/Town/Suburb</Trans>} value={addressFields.addressLocality} />
        <SingleField
          name={<Trans>Unit/Suite/Building</Trans>}
          value={addressFields.addressStreetSubpremise}
        />
        <SingleField name={<Trans>State</Trans>} value={addressFields.addressState} />
        <SingleField
          name={<Trans>Postcode</Trans>}
          value={normalisePostcodeForDisplay(addressFields.addressPostcode)}
          warning={postcodeWarnings?.addressWarning}
        />
      </SimpleGrid>

      <FormModalWrapper
        modalContentHeight="auto"
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        modalContent={
          <AddressAutocomplete
            isEdit={true}
            isSameAsCurrent={addressFields.sameAsCurrent ?? true}
            googleAddressApiKey={googleAddressApiKey}
            addressFields={addressFields}
            onUpdate={handleUpdate}
            getAddressSuggestions={getAddressSuggestions}
          />
        }
      />
    </Box>
  );
}
